import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { Box, Button, Card, TextField } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ConfirmDelete from '../../../shared/components/ConfirmDelete';
import CustomDataGrid from '../../../shared/components/CustomDataGrid';
import { ERole } from '../../../shared/enums/user/ERole';
import { handleError } from '../../../shared/functions/handleError';
import { usePagination } from '../../../shared/hooks/usePagination';
import { useRefundColumns } from '../../../shared/hooks/useRefundColumns';
import { IRefund } from '../../../shared/interfaces/refund/IRefund';
import { RootState } from '../../../store/store';
import { AdminRefundService } from './service';

const AdminRefund = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userData } = useSelector((state: RootState) => state.auth);
  const [data, setData] = useState<IRefund[] | null>(null);
  const [selectedRefund, setSelectedRefund] = useState<IRefund | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const {
    pagination,
    count,
    setPagination,
    totalPages,
    setTotalPages,
    setCount,
    changeFilterHandler,
    handleUpdateList,
  } = usePagination({
    perPage: 15,
  });
  const columns = useRefundColumns(setSelectedRefund, setShowDeleteModal);
  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedRefund(null);
  };

  const deleteHandler = (id?: number) => {
    !!id &&
      AdminRefundService.delete(id)
        .then((response) => {
          if (response) {
            handleUpdateList();
            enqueueSnackbar(
              t('toasts.successfullyDeleted', { title: selectedRefund?.title }),
              {
                variant: 'success',
              },
            );
            closeDeleteModal();
          }
        })
        .catch((err) => handleError(err));
  };

  useEffect(() => {
    AdminRefundService.get({ ...pagination, page: pagination?.page + 1 })
      .then((response) => {
        setCount(response?.count);
        setTotalPages(Math.ceil(response?.count / response?.perPage));
        setData(response.data);
      })
      .catch((err) => handleError(err));
  }, [pagination, setTotalPages, setCount]);

  return (
    <Card>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingBottom: '20px',
        }}
      >
        <TextField
          onChange={(e) => {
            changeFilterHandler('name', e.target.value);
          }}
          label={t('general.searchByName')}
          sx={{
            width: { xs: '100%', md: 'auto' },
            paddingBottom: { xs: 2, md: 0 },
          }}
        />

        <Button
          variant={'contained'}
          onClick={() =>
            navigate(
              `/${userData?.role === ERole.USER ? 'user' : 'admin'}/refund/create`,
            )
          }
          sx={{ width: { xs: '100%', md: 'auto' } }}
        >
          <AddCircleOutlineOutlinedIcon sx={{ marginRight: '5px' }} />
          {t('button.addNewRefund')}
        </Button>
      </Box>
      <CustomDataGrid
        hasNextPage={totalPages < pagination?.page}
        rows={data || []}
        columns={columns}
        rowCount={count}
        pagination={pagination}
        setPagination={setPagination}
        loading={data === undefined}
      />

      <ConfirmDelete
        open={showDeleteModal}
        title={selectedRefund?.title}
        handleClose={closeDeleteModal}
        handleSubmit={() => deleteHandler(selectedRefund?.id)}
      />
    </Card>
  );
};

export default AdminRefund;
